import { addDatadogAction } from '@cartwhl/tracking/datadog';
import { DATADOG_ACTIONS } from '@cartwhl/tracking/datadogConstants';
import { useState } from 'react';

import { buildRedirectUrl } from '@cartwhl/common/stores/authStore';
import { getStudioUrl, stashPendingSignupInfo } from '@cartwhl/common/utils';
import Input from '@cartwhl/ui/atoms/Input';
import Select from '@cartwhl/ui/atoms/Select';
import Text from '@cartwhl/ui/base/Text';
import BorderedCheckmark from '@cartwhl/ui/icons/BorderedCheckmark';
import WarningIcon from '@cartwhl/ui/icons/WarningIcon';
import GoButton from '@cartwhl/ui/input/GoButton';

import { authStore } from 'stores/global';

import {
  INDUSTRY_FOCUS_OPTIONS,
  PRODUCT_FOCUS_OPTIONS,
} from './signupQuestionOptions';

export default function SignupEmailForm() {
  const [loading, setLoading] = useState(false);
  const [isPasswordEmpty, setIsPasswordEmpty] = useState(true);
  const [isPasswordLengthMet, setIsPasswordLengthMet] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);
  const [industryFocus, setIndustryFocus] = useState('');
  const [productFocus, setProductFocus] = useState('');

  const canContinue = industryFocus !== '' && productFocus !== '';

  function handlePasswordOnChange(password: string) {
    setIsPasswordEmpty(password.length === 0);
    setShowPasswordRequirements(true);
    setIsPasswordLengthMet(password.length >= 10);
  }

  function handlePasswordOnBlur() {
    setShowPasswordRequirements(!isPasswordEmpty);
  }

  return (
    <form
      className="grid grid-cols-1 gap-x-4 gap-y-6 pb-7 md:grid-cols-2"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!canContinue) {
          return;
        }
        const email = e.currentTarget.email.value;
        const password = e.currentTarget.password.value;
        const firstname = e.currentTarget.firstname.value;
        const lastname = e.currentTarget.lastname.value;
        stashPendingSignupInfo({ industryFocus, productFocus });
        setLoading(true);
        const result = await authStore.signupWithPassword(
          firstname,
          lastname,
          email,
          password,
        );
        if (result) {
          addDatadogAction(DATADOG_ACTIONS.JOIN_NOW);
          const redirectTo = new URLSearchParams(window.location.search).get(
            'redirectTo',
          );
          if (redirectTo) {
            window.location.href = buildRedirectUrl(redirectTo);
          } else {
            window.location.href = getStudioUrl();
          }
        }
        setLoading(false);
      }}
    >
      <Input
        className="mt-4"
        label="First name"
        name="firstname"
        placeholder="First"
        autoComplete="given-name"
      />
      <Input
        className="mt-4"
        label="Last name"
        name="lastname"
        placeholder="Last"
        autoComplete="family-name"
      />
      <Input
        label="Your email"
        type="email"
        name="email"
        placeholder="Email"
        autoComplete="email"
        className="md:col-span-2"
      />
      <Input
        label="Create a password"
        name="password"
        type="password"
        placeholder="Password"
        autoComplete="new-password"
        className="md:col-span-2"
        onChange={(e) => handlePasswordOnChange(e.target.value)}
        onBlur={() => handlePasswordOnBlur()}
      />
      {showPasswordRequirements && (
        <div className="-mt-4 flex items-center md:col-span-2">
          {isPasswordLengthMet ? (
            <BorderedCheckmark className="text-green mr-2" />
          ) : (
            <WarningIcon className="text-gray-45 mr-2" />
          )}
          <Text
            variant="web/body/md"
            className={isPasswordLengthMet ? 'text-gray-45' : 'text-red'}
          >
            Must be at least 10 characters long
          </Text>
        </div>
      )}
      <Text
        as="label"
        variant="studio/body/medium/sm"
        className="text-gray-90 mt-4 flex flex-col gap-2.5 md:col-span-2 dark:text-white"
      >
        What do you create?
        <Select
          value={industryFocus}
          onValueChange={setIndustryFocus}
          placeholder="Select an option"
          items={INDUSTRY_FOCUS_OPTIONS}
          itemVariant="subtle"
        />
      </Text>
      <Text
        as="label"
        variant="studio/body/medium/sm"
        className="text-gray-90 flex flex-col gap-2.5 md:col-span-2 dark:text-white"
      >
        Where will your motion end up?
        <Select
          value={productFocus}
          onValueChange={setProductFocus}
          placeholder="Select an option"
          items={PRODUCT_FOCUS_OPTIONS}
          itemVariant="subtle"
        />
      </Text>
      <div
        className={`flex justify-end md:col-span-2 ${showPasswordRequirements ? 'mt-3' : 'mt-8'}`}
      >
        <GoButton
          type="submit"
          data-dd-action-name={DATADOG_ACTIONS.JOIN_NOW}
          loading={loading}
          active={canContinue}
          disabled={!canContinue || loading}
          className="min-w-[120px]"
        >
          Continue
        </GoButton>
      </div>
    </form>
  );
}
